import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { scroller, Link } from "react-scroll";

import EditScreenImageTextPlacement from "../../Reusables/EditScreenImageTextPlacement";
import gallery_icon from "../../../assets/images/icons/gallery_ace.svg";
import EditScreenSelector from "../../Reusables/EditScreenSelector";
import EditScreenTextPositionSelection from "../../Reusables/EditScreenTextPositionSelection";

import styles from "./WPR_Reusable.module.css";
import html2canvas from "html2canvas-support-css";

const WoodenPlateRectangleReusable = React.forwardRef((props, ref) => {
  const {
    index,
    callback,
    state,
    image_selection_popup,
    saveThisProduct,
    changeCopyDetails,
    selectedCopyDetails,
    currentPageNumber,
    totalPageNumber,
    confirmEdits,
    generatePreviewsAtBottom,
    preview_copied_image,
  } = props;
  //refs
  const divRef = useRef(null); // It contains the uploaded image
  const containerRef = useRef(null); // It refers to the container of the uploaded image
  const imgRef = useRef(null); // It refers to the uploaded image
  const canvasRef = useRef(null); // It refers to the canvas where the preview image is shown
  const previewRef = useRef(null); // It refers to the full preview image with text

  //parent controlled states
  const {
    hasText,
    imageHasBeenUploaded,
    uploadedImage,
    letteringFont,
    letteringColor,
    letteringContent,
    letteringPosition,
  } = state;

  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setPanning] = useState(false);
  const [image, setImage] = useState();

  const [editedImgSrc, setEditedImgSrc] = useState("");
  const [imgRect, setImgRect] = useState(null);
  const [divRect, setDivRect] = useState(null);
  const [reworkedWidth, setReworkedWidth] = useState(450);
  const [reworkedHeight, setReworkedHeight] = useState(450);

  const [showPreview, setShowPreview] = useState(false);
  const [uploadedImageVertical, setUploadedImageVertical] = useState(false);
  const [uploadedImgSquare, setUploadedImgSquare] = useState(false);
  const [position, setPosition] = useState({
    oldX: 0,
    oldY: 0,
    x: 0,
    y: 0,
    z: 1,
  });

  const resetPosition = () => {
    setPosition({
      oldX: 0,
      oldY: 0,
      x: 0,
      y: 0,
      z: 1,
    });
  };

  const changeState = (key, value) => {
    state[key] = value;
    callback(state);
  };

  const changeStateMultiple = (keys, values) => {
    for (let i = 0; i < keys.length; i++) {
      state[keys[i]] = values[i];
    }
    callback(state);
  };

  const checkImage = () => {
    if (!divRect || !imgRect || !containerRef || !containerRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = uploadedImageVertical ? 400 : 485;
    canvas.height = uploadedImageVertical ? 485 : 400;

    const imageWidth = divRect.width;
    const imageHeight = divRect.height;

    const centerX = imageWidth / 2 + position.x;
    const centerY = imageHeight / 2 + position.y;
    ctx.translate(centerX, centerY);
    ctx.scale(position.z, position.z);
    ctx.translate(-centerX, -centerY);
    ctx.translate(position.x, position.y);
    if (uploadedImageVertical) {
      ctx.drawImage(imgRef.current, 0, 0, canvas.width - 50, canvas.height);
    } else {
      ctx.drawImage(
        imgRef.current,
        0,
        0,
        canvas.width + 40,
        canvas.height - 50
      );
    }
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    ctx.putImageData(imageData, 0, 0);
  };

  const handleShowPreview = () => {
    if (selectedCopyDetails !== currentPageNumber) {
      setShowPreview(true);
      return;
    }

    checkImage();

    const canvas = canvasRef.current;
    canvas.toBlob(function (blob) {
      const url = URL.createObjectURL(blob);
      setEditedImgSrc(url);
      setShowPreview(true);
    });
  };

  const onLoad = (e) => {
    setImage({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
    });
    const aspectRatio = e.target.naturalWidth / e.target.naturalHeight;
    console.log(aspectRatio, e.target.naturalWidth, e.target.naturalHeight);
    if (aspectRatio > 1) {
      setUploadedImageVertical(false);
      setReworkedWidth(350 * aspectRatio);
      setReworkedHeight(350);
      setPosition({
        ...position,
        y: 25,
      });
    } else if (aspectRatio < 1) {
      setUploadedImageVertical(true);
      setReworkedHeight(350 / aspectRatio);
      setReworkedWidth(350);
      setPosition({
        ...position,
        x: 25,
      });
    } else {
      setUploadedImgSquare(true);
      setUploadedImageVertical(false);
      setReworkedHeight(350);
      setReworkedWidth(350);
    }
    setImgRect(imgRef.current.getBoundingClientRect());
    setDivRect(divRef.current.getBoundingClientRect());
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    setPanning(true);
    setPosition({
      ...position,
      oldX: e.clientX,
      oldY: e.clientY,
    });
  };

  const zoom = (val) => {
    let zl = zoomLevel;
    if ((zoomLevel === 6 && val < 0) || (zoomLevel === 0.01 && val > 0)) {
      return;
    } else {
      zl = val < 0 ? zoomLevel + 1 : zoomLevel - 1;
    }
    setZoomLevel(zl);
    const sign = val;
    const scale = 1 - sign;
    const rect = containerRef.current.getBoundingClientRect();
    const cx = (rect["left"] + rect["right"]) / 2;
    const cy = (rect["top"] + rect["bottom"]) / 2;

    if (zl === 1) {
      resetPosition();
    } else {
      setPosition({
        ...position,
        x: position.x * scale - (rect.width / 2 - cx + rect.x) * sign,
        y:
          position.y * scale -
          ((image.height * rect.width) / image.width / 2 - cy + rect.y) * sign,
        z: val > 0 && zoomLevel - 2 === 0 ? 1 : position.z * scale,
      });
    }
  };

  useEffect(() => {
    const mouseup = () => {
      setPanning(false);
    };

    const mousemove = (event) => {
      if (isPanning) {
        let newX = position.x + event.clientX - position.oldX;
        let newY = position.y + event.clientY - position.oldY;
        setPosition({
          ...position,
          x: newX,
          y: newY,
          oldX: event.clientX,
          oldY: event.clientY,
        });
      }
    };

    window.addEventListener("mouseup", mouseup);
    window.addEventListener("mousemove", mousemove);

    return () => {
      window.removeEventListener("mouseup", mouseup);
      window.removeEventListener("mousemove", mousemove);
    };
  });

  const changePreview = async () => {
    try {
      const canvas = await html2canvas(previewRef.current, {
        logging: true,
        letterRendering: 1,
        allowTaint: true,
        useCORS: true,
        scale: 1,
      });
      console.log("saving image again");
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          console.log("generating the file");
          const file = new File([blob], "image.png", { type: "image/jpg" });
          console.log(file);
          resolve(file);
        });
      });
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const uploadNewImage = () => {
    // saveState();
    image_selection_popup(index, 1);
  };

  useImperativeHandle(ref, () => ({
    changePreview,
  }));

  const copyOptions = [];

  for (let i = 1; i < currentPageNumber; i++) {
    copyOptions.push(i + "個目と同じ");
  }

  copyOptions.push("別のデザインで作る");

  let copySelected = selectedCopyDetails + "個目の写真";

  const extractNumber = (s, currentPageNumber, totalPageNumber) => {
    for (let i = totalPageNumber; i > 0; i--) {
      if (s.includes(i)) {
        return i;
      }
    }
    return currentPageNumber;
  };

  const dynamicStyles = {
    uploadedImgContainerSize: {},
    uploadedImgStyle: {},
    uploadedImgBlackBorderCont: {},
    imageEditFrame: {},
    widthOfSelectedTextPosCont: 0,
    heightOfSelectedTextPosCont: 0,
    widthOfEditScreenImgTxtPlacement: 0,
    heightOfEditScreenImgTxtPlacement: 0,
    widthOfPreviewImgWrapper: 0,
    heightOfPreviewImgWrapper: 0,
  };
  if (!uploadedImgSquare) {
    if (uploadedImageVertical) {
      dynamicStyles.uploadedImgContainerSize = {
        width: "400px",
        height: "485px",
      };
      dynamicStyles.uploadedImgStyle = {
        minWidth: "",
        maxWidth: "350px",
        minHeight: reworkedHeight + "px",
        maxHeight: "",
      };
      dynamicStyles.uploadedImgBlackBorderCont = {
        width: "350px",
        height: "435px",
      };
      dynamicStyles.imageEditFrame = {
        width: "400px",
        height: "485px",
      };
      dynamicStyles.widthOfSelectedTextPosCont = 264;
      dynamicStyles.heightOfSelectedTextPosCont = 367;
      dynamicStyles.widthOfEditScreenImgTxtPlacement = 500;
      dynamicStyles.heightOfEditScreenImgTxtPlacement = 621;
      dynamicStyles.widthOfPreviewImgWrapper = 550;
      dynamicStyles.heightOfPreviewImgWrapper = 671;
    } else {
      dynamicStyles.uploadedImgContainerSize = {
        height: "400px",
        width: "485px",
      };
      dynamicStyles.uploadedImgStyle = {
        minWidth: reworkedWidth + "px",
        maxWidth: "",
        minHeight: "",
        maxHeight: "350px",
      };
      dynamicStyles.uploadedImgBlackBorderCont = {
        height: "350px",
        width: "435px",
      };
      dynamicStyles.imageEditFrame = {
        width: "485px",
        height: "400px",
      };
      dynamicStyles.widthOfSelectedTextPosCont = 367;
      dynamicStyles.heightOfSelectedTextPosCont = 264;
      dynamicStyles.widthOfEditScreenImgTxtPlacement = 621;
      dynamicStyles.heightOfEditScreenImgTxtPlacement = 500;
      dynamicStyles.widthOfPreviewImgWrapper = 671;
      dynamicStyles.heightOfPreviewImgWrapper = 550;
    }
  } else {
    dynamicStyles.uploadedImgContainerSize = {
      height: "400px",
      width: "485px",
      display: "flex",
      "justify-content": "center",
      "align-items": "center",
    };
    dynamicStyles.uploadedImgStyle = {
      // minWidth: reworkedWidth + "px",
      // maxWidth: "",
      // minHeight: "",
      // maxHeight: "350px",
      width: "435px",
      height: "350px",
    };
    dynamicStyles.uploadedImgBlackBorderCont = {
      height: "350px",
      width: "435px",
    };
    dynamicStyles.imageEditFrame = {
      width: "485px",
      height: "400px",
    };
    dynamicStyles.widthOfSelectedTextPosCont = 367;
    dynamicStyles.heightOfSelectedTextPosCont = 264;
    dynamicStyles.widthOfEditScreenImgTxtPlacement = 621;
    dynamicStyles.heightOfEditScreenImgTxtPlacement = 500;
    dynamicStyles.widthOfPreviewImgWrapper = 671;
    dynamicStyles.heightOfPreviewImgWrapper = 550;
  }

  return (
    <div className={styles.main_div}>
      <div className={styles.top_hr}></div>
      <div className={styles.header_section_main}>
        <div name="編　集" className={styles.header_section}>
          編　　集
        </div>
        {totalPageNumber > 1 && (
          <div
            className={styles.header_number}
            name={"editsection" + currentPageNumber}
          >
            {currentPageNumber + "個目"}
          </div>
        )}
      </div>
      {currentPageNumber > 1 && (
        <div className={styles.multi_page_selector}>
          <EditScreenSelector
            takeupspace={true}
            options={copyOptions}
            callback={(value) => {
              console.log(value);
              changeCopyDetails(
                extractNumber(value, currentPageNumber, totalPageNumber),
                index
              );
            }}
            selected={copySelected}
          />
        </div>
      )}
      {(selectedCopyDetails === currentPageNumber ||
        currentPageNumber === 1) && (
        <div className={styles.edit_body_section}>
          <div className={styles.edit_image_section}>
            <div className={styles.edit_title_text_01}>
              1. 写真を選択し、拡大・位置調整をしてください。
            </div>
            {!imageHasBeenUploaded && (
              <div
                onClick={uploadNewImage}
                className={styles.upload_image_placeholder}
              >
                <img
                  className={styles.upload_image_gallery_icon}
                  src={gallery_icon}
                  alt="top_gallery"
                  crossOrigin="anonymous"
                />

                <div className={styles.upload_image_placeholder_text}>
                  写真選択
                </div>
              </div>
            )}
            {imageHasBeenUploaded && (
              <div
                style={dynamicStyles.uploadedImgContainerSize}
                className={styles.col_1_image_square}
              >
                <div
                  style={dynamicStyles.uploadedImgContainerSize}
                  className={styles.pan_and_zoom_container}
                  ref={containerRef}
                  onMouseDown={onMouseDown}
                >
                  <div
                    ref={divRef}
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px) scale(${position.z})`,
                    }}
                  >
                    <img
                      ref={imgRef}
                      style={dynamicStyles.uploadedImgStyle}
                      className={styles.pan_and_zoom_image}
                      alt="floorplan"
                      src={uploadedImage}
                      onLoad={onLoad}
                      crossOrigin="anonymous"
                    />
                  </div>
                  <div
                    style={dynamicStyles.uploadedImgBlackBorderCont}
                    className={styles.edit_image_blackborder}
                  ></div>
                  <div
                    style={dynamicStyles.imageEditFrame}
                    className={styles.edit_image_frame}
                  ></div>
                </div>
                <div
                  id={"cross_div_" + index.toString()}
                  onClick={() => {
                    console.log("Cross the image");
                    console.log("changing multiple states");
                    changeStateMultiple(
                      ["uploadedImage", "imageHasBeenUploaded", "showPreview"],
                      ["", false, false]
                    );
                    setShowPreview(false);
                  }}
                  className={styles.cross_div}
                >
                  <ion-icon
                    className={styles.cross_icon}
                    name="close"
                  ></ion-icon>
                </div>
              </div>
            )}
            {imageHasBeenUploaded && (
              <div className={styles.zoom_section}>
                <i
                  onClick={() => {
                    zoom(0.1);
                  }}
                  className={
                    "fa fa-search-minus " +
                    styles.zoom_icon +
                    " " +
                    styles.minus_icon
                  }
                  aria-hidden="true"
                ></i>
                <i
                  onClick={() => {
                    zoom(-0.1);
                  }}
                  className={
                    "fa fa-search-plus " +
                    styles.zoom_icon +
                    " " +
                    styles.plus_icon
                  }
                  aria-hidden="true"
                ></i>
              </div>
            )}
            {imageHasBeenUploaded && (
              <div className={styles.uploaded_image_text_01}>
                <div>黒枠が出来上がり線です。</div>
                <div className={styles.uploaded_image_text_01_div2}>
                  カーソルを移動し、位置を調整してください。
                </div>
              </div>
            )}
          </div>
          <div className={styles.edit_form_section}>
            <div className={styles.edit_form_header_02}>
              2. 文字入れの内容を選択・ご記入ください
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <div className={styles.radio_section}>
              <div className={styles.element}>
                <div className={styles.radio_button}>
                  <input
                    className={styles.radio}
                    type="radio"
                    name={"hastext" + index.toString()}
                    checked={hasText}
                    onChange={() => {}}
                    onClick={(event) => {
                      changeState("hasText", true);
                    }}
                  />
                </div>
                <div className={styles.radio_text}>文字入れ希望</div>
              </div>
              <div className={styles.element + " " + styles.radio_button_2}>
                <div className={styles.radio_button}>
                  <input
                    className={styles.radio}
                    type="radio"
                    checked={!hasText}
                    onChange={() => {}}
                    onClick={(event) => {
                      changeState("hasText", false);
                    }}
                    name={"hastext" + index.toString()}
                  />
                </div>
                <div className={styles.radio_text}>文字入れなし</div>
              </div>
            </div>
            {hasText && (
              <div className={styles.edit_form_section02}>
                <div className={styles.font_select_section}>
                  <div className={styles.font_label}>文字入れフォント</div>
                  <div className={styles.font_option}>
                    <EditScreenSelector
                      selected={letteringFont}
                      options={[
                        "1.クラシカル明朝体",
                        "2.シンプルゴシック",
                        "3.手書きゴシック",
                        "4.やわらか筆記体",
                        "5.クール筆記体",
                        "6.丸め筆記体",
                        "7-1.レトロ明朝体・横書き",
                        "7-2.レトロ明朝体・縦書き",
                        "8-1.丸ゴシック体・横書き",
                        "8-2.丸ゴシック体・縦書き",
                        "9-1.手書きゴシック体・横書き",
                        "9-2.手書きゴシック体・縦書き",
                      ]}
                      callback={(value) => {
                        changeState("letteringFont", value);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.color_select_section}>
                  <div className={styles.color_label}>文字入れカラー</div>
                  <div className={styles.color_option}>
                    <EditScreenSelector
                      selected={letteringColor}
                      options={["1.グレー", "2.ベージュ", "3.白", "4.黒"]}
                      callback={(value) => {
                        changeState("letteringColor", value);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.content_select_section}>
                  <div className={styles.content_label}>
                    <div className={styles.content_label_01}>文字入れ内容 </div>

                    <div className={styles.content_label_02}>
                      （名前・日付など）
                    </div>
                  </div>
                  <div className={styles.content_option}>
                    <textarea
                      placeholder={`例）Misaki                                                \u00A0\u00A0\u00A0\u00A0\u00A0\u00A0 Happy Half Birthday`}
                      type="textbox"
                      rows="3"
                      value={letteringContent}
                      onChange={(event) => {
                        changeState("letteringContent", event.target.value);
                      }}
                      className={styles.input_box}
                    ></textarea>
                  </div>
                </div>
                <div className={styles.position_selection_section}>
                  <div className={styles.position_label}>文字入れエリア</div>
                  <div className={styles.position_option}>
                    <div className={styles.position_option_subtitle}>
                      1〜8の箇所をお選びください。クリックすると色が変わります。
                    </div>
                    <div className={styles.position_selection_square}>
                      <EditScreenTextPositionSelection
                        selectedPosition={letteringPosition}
                        setSelectedPosition={(pos) => {
                          changeState("letteringPosition", pos);
                        }}
                        width={dynamicStyles.widthOfSelectedTextPosCont}
                        height={dynamicStyles.heightOfSelectedTextPosCont}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={styles.edit_done_button_div}>
        <div className={styles.selectable_section}>
          すべてを選択後、プレビューボタンをクリックしてください。
        </div>
        <button className={styles.edit_done_button} onClick={handleShowPreview}>
          デザインプレビュー
        </button>
      </div>
      {showPreview && (
        <div className={styles.preview_section_2}>
          <div className={styles.preview_header_text_section}>
            <div className={styles.preview_header_2}>イメージプレビュー</div>
            {totalPageNumber > 1 && (
              <div
                className={styles.header_number_3}
                name={"editsection" + currentPageNumber}
              >
                {currentPageNumber + "個目"}
              </div>
            )}
          </div>
          <div className={styles.preview_column_2_wrapper}>
            <div className={styles.preview_subtitle}>
              <div>実際の商品は、写真とのバランスを見て文字を配置します。</div>
              <div>文字が長い場合、プレビュー画面では表示されません。</div>
              <div>
                文字サイズ、カラー濃度はお任せとなりますのでご了承ください。
              </div>
            </div>
            {selectedCopyDetails === currentPageNumber && (
              <div ref={previewRef} className={styles.preview_column}>
                <EditScreenImageTextPlacement
                  img={editedImgSrc}
                  letteringPosition={letteringPosition}
                  width={dynamicStyles.widthOfEditScreenImgTxtPlacement}
                  height={dynamicStyles.heightOfEditScreenImgTxtPlacement}
                  letteringColor={letteringColor}
                  letteringFont={letteringFont}
                  letteringContent={letteringContent}
                  uploadedImgSquare={uploadedImgSquare}
                />
              </div>
            )}
            {selectedCopyDetails !== currentPageNumber && (
              <div
                className={styles.copied_preview_image_wrapper}
                style={{
                  width: dynamicStyles.widthOfPreviewImgWrapper,
                  height: dynamicStyles.heightOfPreviewImgWrapper,
                }}
              >
                <div
                  className={styles.copied_preview_image_border}
                  style={{
                    width: dynamicStyles.widthOfPreviewImgWrapper,
                    height: dynamicStyles.heightOfPreviewImgWrapper,
                  }}
                ></div>
                <img
                  className={styles.copied_preview_image}
                  src={
                    preview_copied_image
                      ? URL.createObjectURL(preview_copied_image)
                      : ""
                  }
                  onLoad={onLoad}
                  width={dynamicStyles.widthOfPreviewImgWrapper}
                  height={dynamicStyles.widthOfPreviewImgWrapper}
                  alt="pv_img"
                  crossOrigin="anonymous"
                />
              </div>
            )}
            {totalPageNumber > 1 && (
              <div
                className={styles.save_tag}
                onClick={() => {
                  saveThisProduct();
                }}
              >
                {currentPageNumber + "個目を保存"}
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.confirm_edits_wrapper}>
        <div className={styles.confirm_edits}>
          <div className={styles.confirm_header}>
            上記の内容でデザイン決定しますか？
          </div>
          <div className={styles.confirm_button_section}>
            <Link
              to={
                totalPageNumber === 1
                  ? "編　集"
                  : "editsection" + currentPageNumber
              }
              spy={true}
              smooth={true}
              offset={-350}
              duration={500}
            >
              <button className={styles.confirm_button_no}>編集へ戻る</button>
            </Link>
            <button
              onClick={() => {
                console.log(confirmEdits);
                if (totalPageNumber === 1) {
                  confirmEdits();
                } else {
                  if (currentPageNumber < totalPageNumber) {
                    const elementName = "editsection" + (currentPageNumber + 1);

                    scroller.scrollTo(elementName, {
                      spy: true,
                      smooth: true,
                      offset: -100,
                      duration: 500,
                    });
                  } else {
                    generatePreviewsAtBottom();
                  }
                }
              }}
              className={styles.confirm_button_yes}
            >
              はい
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
export default WoodenPlateRectangleReusable;
